import React, { useEffect, useState } from 'react';
import { ChevronLeftUniversal } from '@duxton-web/icons';
import {
  Button,
  ConfigProvider,
  Form,
  Input,
  Modal,
  notification,
  Typography,
} from '@duxton-web/ui';

import { HTTP_STATUS } from '@/constants';
import { useVariableDispatchContext } from '@/features/variable/contexts/variable-context';
import { updateVariableProductEntity } from '@/features/variable/store/actions/variable/action-creators';
import { useCreateProductEntity } from '@/hooks';
import { FormatErrorMessage } from '@/services/Format';
import { error } from '@/utils/logging';
import { capitalizeFirstLetter } from '@/utils/string';
import { formatHasAccessErrorGeneric } from '@/utils/url';

const OBJECT_EXISTS_ERR = 'product entity already exists in database';
const OBJECT_EXISTS_ERROR_MSG =
  'Product Name already exists. Please return to previous page to add it.';

const CreateEntityModalTitle = ({
  isLoading,
  onCancel,
}: {
  isLoading: boolean;
  onCancel: () => void;
}) => {
  return (
    <div
      style={{
        display: 'flex',
        gap: '8px',
        alignItems: 'center',
      }}
    >
      <ConfigProvider
        theme={{
          components: {
            Typography: {
              titleMarginBottom: 0,
            },
          },
        }}
      >
        <Button
          data-testid="create-entity-back"
          shape="circle"
          ghost
          onClick={onCancel}
          disabled={isLoading}
          type="secondary"
          size="small"
          icon={<ChevronLeftUniversal />}
        />
        <Typography.Title data-testid="create-entity-title" level={3}>
          New Product Name
        </Typography.Title>
      </ConfigProvider>
    </div>
  );
};

const CreateEntityModal = ({
  open = false,
  onOk = () => {},
  onCancel = () => {},
}: {
  open?: boolean;
  onOk?: () => void;
  onCancel?: () => void;
}) => {
  const variableDispatch = useVariableDispatchContext();

  const [productEntityForm] = Form.useForm();
  const values = Form.useWatch([], productEntityForm);

  const [valid, setValid] = useState<boolean>(false);

  let productEntityName = capitalizeFirstLetter(values?.name);

  useEffect(() => {
    productEntityForm.validateFields({ validateOnly: true }).then(
      () => {
        setValid(true);
      },
      () => {
        setValid(false);
      },
    );
  }, [values]);

  const { mutate, isPending } = useCreateProductEntity({
    onSuccess: () => {
      productEntityForm.resetFields();

      notification.success({
        message: 'Product entity created',
        description: `Product entity ${productEntityName} was created successfully`,
      });

      variableDispatch(updateVariableProductEntity(productEntityName));

      onOk();
    },
    onError: (err) => {
      let description = (
        <span>
          {`Product name is not created, due to: ${FormatErrorMessage(error)}`}
        </span>
      );

      const errMsg = err?.response?.data?.arg;
      if (errMsg === OBJECT_EXISTS_ERR) {
        description = <span>{OBJECT_EXISTS_ERROR_MSG}</span>;
      }
      if (err?.response?.status === HTTP_STATUS.FORBIDDEN) {
        description = formatHasAccessErrorGeneric(error);
      }

      notification.error({
        description,
        message: 'Product name creation failed',
      });
    },
  });

  const handleCancel = () => {
    productEntityForm.resetFields();

    onCancel();
  };

  return (
    <Modal
      data-testid="create-entity-modal"
      centered
      mask={false}
      open={open}
      title={
        <CreateEntityModalTitle isLoading={isPending} onCancel={handleCancel} />
      }
      onCancel={handleCancel}
      okText="Add Product Name"
      onOk={() => mutate(productEntityName)}
      closable={false}
      maskClosable={false}
      okButtonProps={{
        size: 'small',
        disabled: !valid || isPending,
      }}
      cancelButtonProps={{
        size: 'small',
        disabled: isPending,
      }}
      width={700}
    >
      <Form
        id="create-entity-form"
        colon={false}
        form={productEntityForm}
        initialValues={{
          name: '',
        }}
        layout="vertical"
        scrollToFirstError
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[
            {
              required: true,
              message: 'Product name is required',
            },
            {
              pattern: new RegExp(/^[a-zA-Z]/),
              message: 'First character should be a letter',
            },
            {
              pattern: new RegExp(/^[a-zA-Z][0-9a-zA-Z_ -]*$/),
              message: 'Only alphanumeric, dash and underscore are allowed',
            },
          ]}
          style={{ margin: 0 }}
        >
          <Input
            id="create-entity-product-name"
            data-testid="create-entity-product-name"
            placeholder="Add Product Name"
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const CreateEntity = ({
  onCreateClick = () => {},
  onOk = () => {},
  onCancel = () => {},
}: {
  onCreateClick?: () => void;
  onOk?: () => void;
  onCancel?: () => void;
}) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <>
      <Button
        data-testid="create-entity-button"
        size="small"
        type="secondary"
        onClick={() => {
          setOpen(true);

          onCreateClick();
        }}
      >
        New Product Name
      </Button>
      <CreateEntityModal
        open={open}
        onOk={() => {
          setOpen(false);

          onOk();
        }}
        onCancel={() => {
          setOpen(false);

          onCancel();
        }}
      />
    </>
  );
};

export default CreateEntity;
